import { cx } from '@vizit/theme';
import * as React from 'react';
import {
  Tab,
  TabList,
  type TabListProps,
  TabPanel,
  type TabPanelProps,
  type TabProps,
  Tabs,
  type TabsProps,
} from 'react-aria-components';
import { tabClasses, tabListClasses } from './styles';
import { TabsContext } from './tabs.context';
import { TabSize, TabVariant } from './types';

interface ExtendedTabsProps extends TabsProps {
  /**
   * The variant of the tabs
   * @optional
   * @default 'default'
   * @type 'default' | 'button' | 'underline'
   */
  variant?: TabVariant;
  /**
   * The size of the tabs
   * @optional
   * @default 'md'
   * @type 'sm' | 'md' | 'lg'
   */
  size?: TabSize;
}

interface ExtendedTabListProps<T extends object> extends TabListProps<T> {
  /**
   * The variant of the tab list. If not provided, inherits from parent Tabs context.
   * @optional
   */
  variant?: TabVariant;
}

interface ExtendedTabProps extends TabProps {
  /**
   * The variant of the tab. If not provided, inherits from parent Tabs context.
   * @optional
   */
  variant?: TabVariant;
  /**
   * The size of the tab. If not provided, inherits from parent Tabs context.
   * @optional
   */
  size?: TabSize;
}

function _Tabs(props: ExtendedTabsProps) {
  const { className, variant = TabVariant.Default, size = TabSize.Md, ...other } = props;

  const contextValue = React.useMemo(() => ({ variant, size }), [variant, size]);

  const widthClass = variant === TabVariant.Underline ? 'w-full' : 'w-fit';

  return (
    <TabsContext value={contextValue}>
      <Tabs className={cx('flex flex-col', widthClass, className)} {...other} />
    </TabsContext>
  );
}

function _TabList<T extends object>(props: ExtendedTabListProps<T>) {
  const { className, variant: variantProp, ...other } = props;
  const { variant: contextVariant } = React.useContext(TabsContext);
  const variant = variantProp ?? contextVariant;

  return <TabList className={cx(tabListClasses({ variant }), className)} {...other} />;
}

function _TabPanel(props: TabPanelProps) {
  const { className, ...other } = props;

  return <TabPanel className={cx('mt-8 outline-none', className)} {...other} />;
}

function _Tab(props: ExtendedTabProps) {
  const { className, variant: variantProp, size: sizeProp, ...other } = props;
  const { variant: contextVariant, size: contextSize } = React.useContext(TabsContext);
  // Allow to override the variant and size from the parent Tabs context
  const variant = variantProp ?? contextVariant;
  const size = sizeProp ?? contextSize;

  return <Tab className={cx(tabClasses({ variant, size }), className)} {...other} />;
}

export { _Tab as Tab, _TabList as TabList, _TabPanel as TabPanel, _Tabs as Tabs };
export type {
  ExtendedTabsProps as TabsProps,
  ExtendedTabListProps as TabListProps,
  ExtendedTabProps as TabProps,
  TabPanelProps,
};
